import { useEffect, useRef, useState } from "react";
import { Typography, Divider, Alert, Box, Stack, TextField, MenuItem } from "@mui/material";
import Grid from "@mui/material/Grid";
import AutocompleteComponent from "../Shared/AutocompleteComponent";
import CountUpComponent from "../Shared/CountUpComponent";
import {
  getData,
  postDataPagination,
} from "../../services/base.service";
import FreeTextComponent from "../Shared/FreeTextComonent";
import { PAGE_LIMIT } from "../../utils/consts";
import {
  ExpertDataArrayType,
  SetExpertDataArrayType,
  SetPageType,
  PageType,
  SetLoadingType,
  FiltersType,
  ReferenceSearchOptionType,
} from "../../types/dataTypes";
import { Info, WorkHistory } from '@mui/icons-material';
import CustomDrawer from "../Shared/CustomDrawer";

// Filters whose value isn't an array (so `.length > 0` doesn't make sense for them) need
// their own "is this filter actively narrowing the results" rule. Keep this list in sync
// with FiltersType's non-array fields.
const isFilterApplied = (key: string, value: any): boolean => {
  if (key === 'referenceId') return value !== '' && value !== undefined && value !== null;
  if (key === 'integrationProject') return value === 'any' || value === 'integration' || value === 'partner';
  return Array.isArray(value) ? value.length > 0 : !!value;
};

type ExpertsFilterProps = {
  data: ExpertDataArrayType;
  setData: SetExpertDataArrayType;
  setFilteredData: SetExpertDataArrayType;
  setPageTotal: SetPageType;
  page: PageType;
  setLoading: SetLoadingType;
  filters: FiltersType;
  setFilters: Function;
  sortConfig: { key: string, direction: string };
  isDrawerOpen: boolean,
  setIsDrawerOpen: Function,
  // Incremented by the parent to re-fetch the current page (same filters/page/sort), e.g. after a save.
  refreshKey?: number
};

function ExpertsFilters({
  data,
  setData,
  setFilteredData,
  setPageTotal,
  page,
  setLoading,
  filters,
  setFilters,
  sortConfig,
  isDrawerOpen,
  setIsDrawerOpen,
  refreshKey = 0
}: ExpertsFilterProps) {

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [availableFilters, setAvailableFilters] = useState(filters);
  const [fullTextSearchValue, setFullTextSearchValue] = useState("");
  const [langSelectectionisAnd] = useState(true);
  const [regionSelectectionisAnd] = useState(true);
  const [nameSelectectionisAnd] = useState(true);
  const [donorSelectectionisAnd] = useState(true);
  const [keywordsSelectectionisAnd, setkeywordsSelectectionisAnd] = useState(true);
  const [nationalitiesSelectectionisAnd] = useState(true);

  // Add this line for error handling
  const [error, setError] = useState<string | null>(null);

  const [numberOfExperts, setNumberOfExperts] = useState(data.length);

  // "Projects worked with" filter: search-as-you-type over GET api/references/search,
  // storing only the picked reference's ID (as filters.referenceId) for the backend filter,
  // while keeping the full {id, label} option locally so the Autocomplete can show it.
  const [referenceSearchOptions, setReferenceSearchOptions] = useState<ReferenceSearchOptionType[]>([]);
  const [referenceSearchLoading, setReferenceSearchLoading] = useState(false);
  const [selectedReferenceOption, setSelectedReferenceOption] = useState<ReferenceSearchOptionType | null>(null);
  const referenceSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const REFERENCE_SEARCH_MIN_LENGTH = 2;

  const handleReferenceSearchInput = (event: any, inputValue: string, reason: string) => {
    if (referenceSearchDebounceRef.current) {
      clearTimeout(referenceSearchDebounceRef.current);
    }
    if (reason === 'reset') return;

    const trimmed = (inputValue || '').trim();
    if (trimmed.length < REFERENCE_SEARCH_MIN_LENGTH) {
      setReferenceSearchOptions([]);
      return;
    }

    referenceSearchDebounceRef.current = setTimeout(async () => {
      setReferenceSearchLoading(true);
      try {
        const results = await getData(`references/search?q=${encodeURIComponent(trimmed)}`);
        setReferenceSearchOptions((results as ReferenceSearchOptionType[]) || []);
      } catch (err) {
        setReferenceSearchOptions([]);
      } finally {
        setReferenceSearchLoading(false);
      }
    }, 300);
  };

  const handleReferenceSelect = (event: any, newValue: any) => {
    const option = newValue && typeof newValue === 'object' ? (newValue as ReferenceSearchOptionType) : null;
    setSelectedReferenceOption(option);
    setFilters((prevFilters: any) => ({ ...prevFilters, referenceId: option ? option.id : '' }));
  };

  // Only the latest request may apply its result / clear the loading state.
  const requestSeqRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const fetchData = async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const seq = ++requestSeqRef.current;
    const isStale = () => seq !== requestSeqRef.current || controller.signal.aborted;

    setLoading(true);
    setError(null); // Clear previous errors

    try {
      const areFiltersApplied = Object.entries(filters).some(
        ([key, value]) => isFilterApplied(key, value)
      );

      if (areFiltersApplied) {
        const filtersApi = Object.keys(filters).reduce(
          (acc, key) => ({
            ...acc,
            [key]: (key === 'integrationProject' || key === 'referenceId')
              ? { isFiltered: isFilterApplied(key, filters[key]), value: filters[key] }
              : {
                isFiltered: isFilterApplied(key, filters[key]),
                values: filters[key],
                isAnd:
                  key === 'languages' ? langSelectectionisAnd :
                  key === 'regions' ? regionSelectectionisAnd :
                  key === 'names' ? nameSelectectionisAnd :
                  key === 'donors' ? donorSelectectionisAnd :
                  key === 'keywords' ? keywordsSelectectionisAnd :
                  key === 'nationalities' ? nationalitiesSelectectionisAnd : false
              },
          }),
          { sortConfig: sortConfig }
        );

        const result = await postDataPagination(
          "experts/filterdata",
          page,
          PAGE_LIMIT,
          { filtersApi },
          controller.signal
        );
        if (isStale()) return;

        // Add this check for backend errors
        if (result.success === false) {
          setError(result.error || 'An error occurred while filtering data');
          setFilteredData([]);
          setPageTotal(0);
          setNumberOfExperts(0);
        } else {
          setFilteredData(result.filteredExpertData);
          setAvailableFilters(result.availableFilters);
          setPageTotal(result.total);
          setNumberOfExperts(result.total);
        }
      } else {
        const result = await postDataPagination(
          "experts/expertsTable",
          page,
          PAGE_LIMIT,
          { sortConfig },
          controller.signal
        );
        if (isStale()) return;
        setData(result.data);
        setFilteredData(result.data);
        setPageTotal(result.total);
        setAvailableFilters(result.availableFilters);
        setNumberOfExperts(result.total);
      }
    } catch (err: any) {
      // A cancelled/superseded request is not an error; the newer request owns the UI state.
      if (err?.name === 'AbortError' || isStale()) return;
      setError(err.message || 'An unexpected error occurred');
    }

    if (!isStale()) setLoading(false);
  };

  useEffect(() => {
    fetchData();
    return () => {
      abortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, sortConfig, refreshKey]);

  const handleFilterChange =
    (filterName: string) => (event: any, newValue: any) => {
      setFilters((prevFilters: any) => ({ ...prevFilters, [filterName]: newValue }));
    };

  const handleBooleanFilterChange =
    (filterName: string) => (event: any) => {
      setFilters((prevFilters: any) => ({ ...prevFilters, [filterName]: event.target.checked }));
    };

  const resetFilters = () => {
    setFullTextSearchValue("");
    setError(null); // Clear errors when resetting
    setSelectedReferenceOption(null);
    setReferenceSearchOptions([]);
    setFilters({
      names: [],
      languages: [],
      regions: [],
      donors: [],
      keywords: [],
      fulltext: [],
      nationalities: [],
      positions: [],
      integrationProject: '',
      referenceId: '',
    });
  };

  const handleFullTextSearch = (searchValue: any) => {
    setFilters((prevFilters: any) => ({ ...prevFilters, fulltext: searchValue }));
  };

  return (
    <div>
      {/* Add this error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} justifyContent="space-between">
        {/* Rest of your existing code stays exactly the same */}
        <Grid item xs={12} md={8}>
          <FreeTextComponent
            onSearch={handleFullTextSearch}
            label="Full Text Search"
            inputValue={fullTextSearchValue}
            setInputValue={setFullTextSearchValue}
            setIsDialogOpen={setIsDialogOpen}
            dialogOpen={isDialogOpen}
          />
          <Grid container spacing={3} paddingTop={2}>
            <Grid item xs={12} md={6}>
              <Grid>
                <AutocompleteComponent
                  options={availableFilters.languages}
                  value={filters.languages}
                  handleChange={handleFilterChange("languages")}
                  isMulti={true}
                  label="Search by Language experience"
                />
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid>
                <AutocompleteComponent
                  options={availableFilters.regions}
                  value={filters.regions}
                  handleChange={handleFilterChange("regions")}
                  isMulti={true}
                  label="Search by regional experience"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <CountUpComponent resetFilters={resetFilters} end={numberOfExperts} />
        </Grid>
      </Grid>
      
      <CustomDrawer open={isDrawerOpen} toggleDrawer={setIsDrawerOpen} vw={20} title={'Advanced Filter'} orientation="left">
        <Stack direction="row" alignItems="center" gap={1} paddingBottom={2}>
          <Info color="action" />
          <Typography variant="h6">General</Typography>
        </Stack>
        <Grid paddingBottom={3}>
          <AutocompleteComponent
            options={availableFilters.names}
            value={filters.names}
            handleChange={handleFilterChange("names")}
            isMulti={true}
            label="Search by Name"
          />
        </Grid>
        <Grid paddingBottom={3}>
          <AutocompleteComponent
            options={availableFilters.donors}
            value={filters.donors}
            handleChange={handleFilterChange("donors")}
            isMulti={true}
            label="Search by Donor worked with"
          />
        </Grid>
        <Grid>
          <AutocompleteComponent
            options={availableFilters.nationalities}
            value={filters.nationalities}
            handleChange={handleFilterChange("nationalities")}
            isMulti={true}
            label="Search by Experts Nationality"
          />
        </Grid>
        <Divider sx={{ marginTop: 2, marginBottom: 2 }} />
        <AutocompleteComponent
            options={availableFilters.keywords}
            value={filters.keywords}
            handleChange={handleFilterChange("keywords")}
            isMulti={true}
            label="Search by Keywords"
            radioState={keywordsSelectectionisAnd}
            setRadioState={setkeywordsSelectectionisAnd}
          />

        <Box
          sx={{
            marginTop: 4,
            marginBottom: 2,
            borderRadius: 1,
          }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <WorkHistory color="action" />
            <Typography variant="h6" >
              Project Experience
            </Typography>
          </Stack>
        </Box>
        <Grid paddingBottom={3}>
          <AutocompleteComponent
            options={availableFilters.positions || []}
            value={filters.positions || []}
            handleChange={handleFilterChange("positions")}
            isMulti={true}
            label="Search by Position held on references"
          />
        </Grid>
        <Grid paddingBottom={3}>
          <TextField
            select
            fullWidth
            size="small"
            label="Worked on an INTEGRATION project"
            value={filters.integrationProject || ''}
            onChange={(event) => setFilters((prevFilters: any) => ({ ...prevFilters, integrationProject: event.target.value }))}
          >
            <MenuItem value=""><em>Not filtered</em></MenuItem>
            <MenuItem value="any">Any (direct or via partner)</MenuItem>
            <MenuItem value="integration">Contracted by INTEGRATION</MenuItem>
            <MenuItem value="partner">Only via a partner</MenuItem>
          </TextField>
        </Grid>
        <Grid>
          <AutocompleteComponent
            options={referenceSearchOptions}
            value={selectedReferenceOption}
            handleChange={handleReferenceSelect}
            isMulti={false}
            label="Search by Project worked with"
            placeholder="Type at least 2 characters (Project Number or Title)"
            getOptionLabel={(option: any) => (option ? String(option.label ?? '') : '')}
            isOptionEqualToValue={(option: any, val: any) => option?.id === val?.id}
            onInputChange={handleReferenceSearchInput}
            loading={referenceSearchLoading}
            filterOptions={(opts: any[]) => opts}
            noOptionsText="Type at least 2 characters to search"
          />
        </Grid>
      </CustomDrawer>
    </div>
  );
}

export default ExpertsFilters;